/**
 * Option Entity Factory
 * Canonical builder for option entities used by both formula and taskpane.
 * 
 * Two modes:
 * - Basic: identity-only properties (display name, underlying, right, exp, strike, occ symbol)
 * - Enriched: Basic + snapshot fields (delta/gamma/theta/vega/iv/last/change/changePct/oi/volume/etc)
 * 
 * Enriched mode fetches snapshot data once and maps it to the same entity contract used by the formula.
 * If snapshot fetch fails, enriched build throws UserInputError; taskpane implements fallback to basic.
 */

/* global Excel */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { parseOccSymbol, type ParsedOccSymbol } from "../../shared/symbols/occOptionSymbol";
import { buildOptionEntityCellValue, type OptionEntityProps } from "../../excel/entities/optionEntity";
import { formatOptionDisplayName, parseOptionRef } from "../../shared/options";
import { getOptionSnapshot } from "../massive";
import type { OptionSnapshot } from "../../shared/options";
import { NotListedError } from "../../shared/errors/notListedError";
import { UserInputError } from "../../shared/errors/UserInputError";

/**
 * Normalize OCC symbol input
 * - Trim, remove ALL whitespace (tabs, newlines, etc.), uppercase
 * - Optionally strip zero-width chars (for pasted symbols from web/Slack)
 */
function normalizeOccSymbolInput(input: string): string {
  // Remove all whitespace (spaces, tabs, newlines, etc.)
  let normalized = input.replace(/\s+/g, "");
  
  // Optionally strip zero-width chars (for pasted symbols from web/Slack)
  normalized = normalized.replace(/[\u200B-\u200D\uFEFF]/g, "");
  
  // Uppercase
  return normalized.toUpperCase();
}

/**
 * Convert YYMMDD to YYYY-MM-DD format
 * Uses same year windowing as coerceExcelDateToYyyyMmDd: 00-79 → 2000-2079, 80-99 → 1980-1999
 */
function occYyMmDdToIso(yymmdd: string): string {
  // Validate YYMMDD is exactly 6 digits
  if (!/^\d{6}$/.test(yymmdd)) {
    throw new Error("Invalid YYMMDD format: " + yymmdd);
  }

  const yy = yymmdd.slice(0, 2);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);

  // Year windowing: 00-79 → 2000-2079, 80-99 → 1980-1999
  const yearNum = parseInt(yy, 10);
  let yyyy: string;
  if (yearNum >= 0 && yearNum <= 79) {
    yyyy = "20" + yy;
  } else if (yearNum >= 80 && yearNum <= 99) {
    yyyy = "19" + yy;
  } else {
    throw new Error("Invalid year in YYMMDD: " + yy);
  }

  return yyyy + "-" + mm + "-" + dd;
}

/**
 * Construct OptionEntityProps from parsed OCC symbol (basic/identity fields only)
 */
function toOptionEntityPropsBasic(parsed: ParsedOccSymbol): OptionEntityProps {
  // Convert expirationYYMMDD to YYYY-MM-DD
  const expiration = occYyMmDdToIso(parsed.expirationYYMMDD);
  
  // Parse for display name (same logic as formula)
  let parsedRef;
  try {
    parsedRef = parseOptionRef(parsed.formats.unspaced);
  } catch {
    // Fallback: build from parsed components
    parsedRef = {
      underlying: parsed.root,
      expiration: expiration,
      strike: parsed.strike.value,
      right: parsed.side,
      occ: parsed.formats.unspaced,
    };
  }
  
  return {
    underlying: parsed.root,
    expiration: expiration,
    strike: parsed.strike.value,
    right: parsed.side,
    display_name: formatOptionDisplayName(parsedRef),
    occ_symbol: parsed.formats.unspaced,
  };
}

/**
 * Build basic option entity from OCC symbol (identity fields only)
 * @param occSymbol - OCC option symbol string
 * @returns Excel EntityCellValue with basic properties
 * @throws UserInputError if symbol is invalid
 */
export function buildOptionEntityBasicFromOccSymbol(occSymbol: string): any {
  // Normalize input (trim, remove ALL whitespace, uppercase)
  const normalized = normalizeOccSymbolInput(occSymbol);
  
  // Parse with parseOccSymbol() to validate (strict - must succeed)
  let parsed: ParsedOccSymbol;
  try {
    parsed = parseOccSymbol(normalized);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid OCC symbol format";
    throw new UserInputError("INVALID_SYMBOL", message, "symbol");
  }
  
  // Build basic props
  const props = toOptionEntityPropsBasic(parsed);
  
  // Add updatedAt timestamp (current time for basic entities)
  props.updatedAt = new Date().toISOString();
  
  // Build entity
  return buildOptionEntityCellValue(props);
}

/**
 * Build enriched option entity from OCC symbol (basic + snapshot data)
 * Fetches snapshot once and maps to the same entity contract used by the formula.
 * @param occSymbol - OCC option symbol string
 * @returns Excel EntityCellValue with enriched properties
 * @throws UserInputError if symbol is invalid or snapshot fetch fails
 */
export async function buildOptionEntityEnrichedFromOccSymbol(occSymbol: string): Promise<any> {
  // First, build basic entity to get parsed components
  const normalized = normalizeOccSymbolInput(occSymbol);
  let parsed: ParsedOccSymbol;
  try {
    parsed = parseOccSymbol(normalized);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Invalid OCC symbol format";
    throw new UserInputError("INVALID_SYMBOL", message, "symbol");
  }
  
  // Build basic props
  const basicProps = toOptionEntityPropsBasic(parsed);
  
  // Fetch snapshot ONCE via existing Massive snapshot service
  let snapRes;
  try {
    snapRes = await getOptionSnapshot(parsed.formats.unspaced);
  } catch (err) {
    if (err instanceof NotListedError) {
      throw new UserInputError("NOT_LISTED", "Not listed", undefined);
    }
    throw err;
  }

  // Handle error result
  if (!snapRes.ok) {
    // Throw UserInputError so taskpane can implement fallback
    throw new UserInputError(
      "SNAPSHOT_FETCH_FAILED",
      "Market data unavailable: " + snapRes.error.message,
      undefined
    );
  }
  
  const snapshot: OptionSnapshot = snapRes.snapshot;
  
  // Map snapshot into the same OptionEntityProps fields used by formula
  // Convert null to undefined for entity builder (it expects undefined for missing values)
  const toValue = <T>(v: T | null): T | undefined => (v === null ? undefined : v);
  
  // Parse option ref for display name (use parsed ref if available, otherwise build from basic props)
  let parsedRef;
  try {
    parsedRef = parseOptionRef(parsed.formats.unspaced);
  } catch {
    parsedRef = {
      underlying: basicProps.underlying,
      expiration: basicProps.expiration,
      strike: basicProps.strike,
      right: basicProps.right,
      occ: basicProps.occ_symbol,
    };
  }
  
  const enrichedProps: OptionEntityProps = {
    // Core identity (from basic)
    underlying: snapshot.underlying ?? basicProps.underlying,
    expiration: basicProps.expiration,
    strike: basicProps.strike,
    right: basicProps.right,
    display_name: formatOptionDisplayName(parsedRef),
    occ_symbol: basicProps.occ_symbol,
    
    // Quote (quote is required, but fields can be null)
    bid: toValue(snapshot.quote.bid),
    ask: toValue(snapshot.quote.ask),
    mid: toValue(snapshot.quote.mid),
    last: toValue(snapshot.quote.last),
    change: toValue(snapshot.quote.change),
    // Convert changePercent from percentage (e.g., 89.011) to decimal (e.g., 0.89011)
    // Massive returns percentages, Excel expects decimals for percentage format
    change_percent: snapshot.quote.changePercent !== null && snapshot.quote.changePercent !== undefined
      ? snapshot.quote.changePercent / 100
      : undefined,
    volume: toValue(snapshot.volume?.value),
    open_interest: toValue(snapshot.openInterest?.value),
    
    // Greeks (greeks can be null)
    delta: toValue(snapshot.greeks?.delta),
    gamma: toValue(snapshot.greeks?.gamma),
    theta: toValue(snapshot.greeks?.theta),
    vega: toValue(snapshot.greeks?.vega),
    rho: toValue(snapshot.greeks?.rho),
    
    // IV (separate from greeks in new model)
    iv: toValue(snapshot.iv?.impliedVolatility),
    
    // Timestamp: use snapshot.asOf if available, else current fetch time
    updatedAt: snapshot.asOf ?? new Date().toISOString(),
  };
  
  // Build and return entity
  return buildOptionEntityCellValue(enrichedProps);
}
